import { DragDropModule } from '@angular/cdk/drag-drop';
import { DatePipe } from '@angular/common';
import { importProvidersFrom } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { NavigationError, provideRouter, withNavigationErrorHandler, withRouterConfig } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { ContractDateTransformPipe } from '../_helpers/transform/contract-date.transform';
import { ContractStatusTransformPipe } from '../_helpers/transform/contract-status.transform';
import { ContractTerminationTypeTransformPipe } from '../_helpers/transform/contract-termination-type.transform';
import { DelegationService } from '../_services/delegation.service';
import { routes } from '../app.routes';

export const coreProviders = [
  importProvidersFrom(BrowserModule, DragDropModule, FormsModule, ReactiveFormsModule),
  provideRouter(
    routes,
    withRouterConfig({ onSameUrlNavigation: 'reload' }),
    withNavigationErrorHandler((e: NavigationError) => {
      // When a lazy-loaded chunk can't be fetched (e.g., after a deployment that replaced the
      // hashed filenames), force a hard reload so the browser picks up the new index.html
      // and its updated chunk references.
      // The sessionStorage flag prevents an infinite reload loop in case the chunk is
      // genuinely missing or a non-stale-cache error (e.g. network failure) it expires after 5min
      const reloadFlagTtlMs = 5 * 60 * 1000;
      const message: string = e.error?.message ?? '';
      const isChunkError =
        message.includes('dynamically imported module') || // Chrome, Firefox
        message.includes('Importing a module script failed') || // Safari
        message.includes('Failed to fetch') ||
        e.error?.name === 'ChunkLoadError';
      if (isChunkError) {
        const chunkUrl = /https?:\/\/\S+\.(?:js|mjs)/.exec(message)?.[0] ?? e.url;
        const storageKey = `chunk-reload:${chunkUrl}`;
        const lastReload = Number(sessionStorage.getItem(storageKey));
        if (!lastReload || Date.now() - lastReload > reloadFlagTtlMs) {
          sessionStorage.setItem(storageKey, String(Date.now()));
          globalThis.location.assign(e.url);
        } else {
          // the reload didn't help
          Sentry.captureException(new Error(`Chunk still failed to load after a reload: ${chunkUrl}`));
        }
      }
    }),
  ),
  DelegationService,
  ContractStatusTransformPipe,
  ContractDateTransformPipe,
  ContractTerminationTypeTransformPipe,
  DatePipe,
  provideAnimations(),
];
